import type { DashboardConfig } from '../data/queries.js';
import { isSurfaceVisibleForSubjects } from './isSurfaceVisible.js';

export type SurfaceRouteRule = {
  prefix: string;
  keys: string[];
};

// Ordine: prima le route specifiche, poi i contenitori generali.
// Una route con più key resta accessibile se almeno una superficie che la espone
// è visibile (es. Documenti dalla tile Home oppure dalla scheda in basso).
export const SURFACE_ROUTE_RULES: SurfaceRouteRule[] = [
  { prefix: '/documents/transport', keys: ['documenti-trasporto'] },
  { prefix: '/warehouse/supply-requests', keys: ['richieste-fornitura'] },
  { prefix: '/warehouse/production-plan', keys: ['pianifica-produzione'] },
  { prefix: '/warehouse/production-todo', keys: ['da-produrre-oggi'] },
  { prefix: '/warehouse/production-destinations', keys: ['smistamento-destinazioni'] },
  { prefix: '/warehouse/production-sheet', keys: ['fabbisogno-produzione'] },
  { prefix: '/warehouse/production-day', keys: ['scheda-produzione'] },
  { prefix: '/warehouse/shipments', keys: ['spedizioni'] },
  { prefix: '/settings/destinations', keys: ['destinazioni'] },
  { prefix: '/settings/menu', keys: ['menu'] },
  { prefix: '/settings/reports', keys: ['settings.reports'] },
  { prefix: '/settings/business', keys: ['settings.business'] },
  { prefix: '/settings/staff', keys: ['settings.staff'] },
  { prefix: '/settings/printer', keys: ['settings.printer'] },
  { prefix: '/settings/sdi', keys: ['settings.sdi'] },
  { prefix: '/business/event-quotes', keys: ['event-quotes'] },
  { prefix: '/haccp/manuale', keys: ['manuale'] },
  { prefix: '/haccp/registri-compila', keys: ['registri-compila'] },
  { prefix: '/haccp/calibrations', keys: ['registri-compila'] },
  { prefix: '/haccp/pest-control', keys: ['pest-control', 'registri-compila'] },
  { prefix: '/haccp/oil-control', keys: ['controllo-olio', 'registri-compila'] },
  { prefix: '/haccp/acrylamide-control', keys: ['controllo-acrilammide', 'registri-compila'] },
  { prefix: '/haccp/formazione', keys: ['registri-compila'] },
  { prefix: '/haccp/non-conformita', keys: ['registri-compila'] },
  { prefix: '/haccp/manutenzione', keys: ['registri-compila'] },
  { prefix: '/haccp/trasporto', keys: ['registri-compila'] },
  { prefix: '/haccp/registri', keys: ['export-registri'] },
  { prefix: '/haccp/temperatures', keys: ['temperature'] },
  { prefix: '/haccp/cleaning', keys: ['pulizie'] },
  { prefix: '/haccp/labels', keys: ['etichetta'] },
  { prefix: '/haccp/expirations', keys: ['scadenze'] },
  { prefix: '/haccp/trace-search', keys: ['tracciabilita'] },
  { prefix: '/haccp/recipes', keys: ['ricette'] },
  { prefix: '/haccp/food-cost', keys: ['food-cost'] },
  { prefix: '/guida', keys: ['guida'] },
  { prefix: '/documents', keys: ['documenti', 'tab.documents'] },
  { prefix: '/warehouse', keys: ['magazzino', 'tab.warehouse'] },
];

// Superfici inline che non hanno una route autonoma.
export const INLINE_SURFACE_KEYS = ['settings.recipe-qty'] as const;

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function routeSurfaceKeys(pathname: string): string[] | null {
  const normalizedPathname = pathname.toLowerCase();
  return SURFACE_ROUTE_RULES.find((rule) => matchesPrefix(normalizedPathname, rule.prefix))?.keys ?? null;
}

export function isSurfaceRouteAllowed(input: {
  pathname: string;
  config: DashboardConfig;
  subjects: readonly string[];
  bypassVisibility: boolean;
  configResolved: boolean;
}): boolean {
  const keys = routeSurfaceKeys(input.pathname);
  if (!keys) return true;
  if (!input.configResolved && !input.bypassVisibility) return false;

  return input.bypassVisibility || keys.some((key) => isSurfaceVisibleForSubjects(input.config, input.subjects, key));
}
